/**
 * Split editor for the Audit screen (design §5.5).
 *
 * Save stays disabled until the lines total the original amount to the cent.
 * Percentages convert to cents on blur. The last line of a multi-line split
 * absorbs rounding remainder when that line was typed as a percent.
 * Tab on the last line opens the next line holding the remainder.
 */
import { mountChartChooser, type ChooserListWords } from "../chart_chooser.js";
import { h } from "../dom.js";
import { decimalToCents, formatCents } from "./money.js";
import type { AccountLabelContext } from "./display-words.js";
import * as face from "./face.js";
import type { AccountPath, CompanySlug } from "../types.js";

export const MAX_SPLIT_LINES = 20;

/**
 * The company picker's own words (rd r12 F1, R5). It is a list of companies,
 * not of the chart's locations, so its empty and no-match lines say so.
 */
export const SPLIT_COMPANY_LIST_WORDS: ChooserListWords = {
  many: "companies",
  noMatch: "No company has that name",
  empty: "No company to choose",
};

const AMOUNT_RE = /^-?(?:0|[1-9]\d{0,12})(?:\.\d{1,2})?$/;
const PERCENT_RE = /^(\d{1,3})(?:\.(\d{1,4}))?%$/;

export interface SplitLineDraft {
  id: string;
  company: CompanySlug;
  location: string;
  account: string;
  amountText: string;
}

export function centsToAmount(cents: bigint): string {
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  const whole = abs / 100n;
  const frac = (abs % 100n).toString().padStart(2, "0");
  return `${negative ? "-" : ""}${whole.toString()}.${frac}`;
}

/** Dollars (`2000`, `40.00`) or a percent of `totalCents` (`40%`). */
export function parseSplitAmount(text: string, totalCents: bigint): bigint | null {
  const trimmed = text.trim();
  const percent = PERCENT_RE.exec(trimmed);
  if (percent) {
    const whole = percent[1] ?? "0";
    const frac = (percent[2] ?? "").padEnd(4, "0");
    const numer = BigInt(whole) * 10000n + BigInt(frac);
    if (numer > 100n * 10000n) return null;
    return (totalCents * numer) / (100n * 10000n);
  }
  if (!AMOUNT_RE.test(trimmed)) return null;
  try {
    return decimalToCents(trimmed);
  } catch {
    return null;
  }
}

/**
 * Cents each line currently contributes. A last-line percent on a multi-line
 * split is `total - sum(previous)` so rounding cannot leave a cent behind.
 */
export function resolvedSplitCents(
  lines: readonly SplitLineDraft[],
  totalCents: bigint,
): (bigint | null)[] {
  const out: (bigint | null)[] = [];
  let prior = 0n;
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (!line) {
      out.push(null);
      continue;
    }
    const text = line.amountText.trim();
    const lastPercent = i === lines.length - 1 && lines.length > 1 && text.endsWith("%");
    if (lastPercent) {
      out.push(totalCents - prior);
      continue;
    }
    const cents = parseSplitAmount(text, totalCents);
    out.push(cents);
    if (cents !== null) prior += cents;
  }
  return out;
}

export function remainingCents(
  lines: readonly SplitLineDraft[],
  totalCents: bigint,
): bigint | null {
  const resolved = resolvedSplitCents(lines, totalCents);
  let sum = 0n;
  for (const cents of resolved) {
    if (cents === null) return null;
    sum += cents;
  }
  return totalCents - sum;
}

export function splitSaveEnabled(
  lines: readonly SplitLineDraft[],
  totalCents: bigint,
  locationAllowed: (company: CompanySlug, location: string) => boolean,
  accountAllowed: (company: CompanySlug, account: string) => boolean,
): boolean {
  if (lines.length === 0 || lines.length > MAX_SPLIT_LINES) return false;
  const resolved = resolvedSplitCents(lines, totalCents);
  let sum = 0n;
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const cents = resolved[i];
    if (!line || cents === undefined || cents === null) return false;
    // The company comes from the picker's own list (or a stored draft), so
    // empty is the only "not chosen" state; `.length` keeps the brand guard's
    // string-method rule off a key.
    if (line.company.length === 0) return false;
    if (!locationAllowed(line.company, line.location)) return false;
    if (!accountAllowed(line.company, line.account)) return false;
    sum += cents;
  }
  return sum === totalCents;
}

/** Convert percent text to cents. Last-line percent becomes the remainder. */
export function applyPercentBlur(
  lines: readonly SplitLineDraft[],
  totalCents: bigint,
): SplitLineDraft[] {
  const next = lines.map((line) => ({ ...line }));
  let prior = 0n;
  for (let i = 0; i < next.length; i += 1) {
    const line = next[i];
    if (!line) continue;
    const text = line.amountText.trim();
    if (!text.endsWith("%")) {
      const cents = parseSplitAmount(text, totalCents);
      if (cents !== null) prior += cents;
      continue;
    }
    const last = i === next.length - 1 && next.length > 1;
    if (last) {
      line.amountText = centsToAmount(totalCents - prior);
      continue;
    }
    const cents = parseSplitAmount(text, totalCents);
    if (cents === null) continue;
    prior += cents;
    line.amountText = centsToAmount(cents);
  }
  return next;
}

export function withTabRemainder(
  lines: readonly SplitLineDraft[],
  index: number,
  totalCents: bigint,
  newId: string,
): SplitLineDraft[] {
  if (index !== lines.length - 1) return lines.map((line) => ({ ...line }));
  if (lines.length >= MAX_SPLIT_LINES) return lines.map((line) => ({ ...line }));
  const current = lines[index];
  if (!current) return lines.map((line) => ({ ...line }));
  const cents = parseSplitAmount(current.amountText, totalCents);
  if (cents === null) return lines.map((line) => ({ ...line }));
  const rest = totalCents - cents;
  if (rest === 0n) return lines.map((line) => ({ ...line }));
  if (totalCents >= 0n && rest < 0n) return lines.map((line) => ({ ...line }));
  return [
    ...lines.map((line) => ({ ...line })),
    {
      id: newId,
      company: current.company,
      location: current.location,
      account: "",
      amountText: centsToAmount(rest),
    },
  ];
}

export interface SplitEditorOptions {
  totalCents: bigint;
  lines: SplitLineDraft[];
  companies: readonly { slug: CompanySlug; label: string }[];
  locationsFor: (company: CompanySlug) => readonly string[];
  accountsFor: (company: CompanySlug) => readonly AccountPath[];
  /**
   * The page's ONE account-label context (rd13 L1): ledger company segment ->
   * the company's plain name. The account picker names an account, so a
   * between-companies one reads "Due from <company>", never its key.
   */
  labelCompanies: AccountLabelContext;
  onSave: (lines: readonly SplitLineDraft[]) => void;
  onCancel: () => void;
}

export interface SplitEditorHandle {
  root: HTMLElement;
  drafts: () => readonly SplitLineDraft[];
}

/** `line:field` — a box holding a name that is not on the list. */
function unlistedKey(lineId: string, field: "company" | "location" | "account"): string {
  return `${lineId}:${field}`;
}

export function mountSplitEditor(opts: SplitEditorOptions): SplitEditorHandle {
  let lines = opts.lines.map((line) => ({ ...line }));
  let seq = lines.length;
  /**
   * R5-04: the box may hold text the draft does not, and the old handler simply
   * kept the old Location/Account/Company while Save stayed enabled — so the
   * save posted a value the person could no longer see. Now an unlisted name
   * marks the row invalid and turns Save off until a listed name is chosen.
   * Nothing is marked while the person is still typing (blur/change only), so
   * a partly typed name never flashes an error.
   */
  const unlisted = new Set<string>();
  const root = h("div", {
    class: "split-editor",
    "data-role": "split-editor",
  });

  const locationAllowed = (company: CompanySlug, location: string): boolean =>
    opts.locationsFor(company).includes(location);
  const accountAllowed = (company: CompanySlug, account: string): boolean =>
    opts.accountsFor(company).includes(account);

  /**
   * Round-11 item 1 (HIGH): the ONE save path. The Save button, the amount
   * input's Enter and any future submit path all call this, so an unlisted
   * company/location/account can never be posted from a path that forgot the
   * `unlisted` check. It returns whether a save happened.
   */
  const splitIsValid = (): boolean =>
    unlisted.size === 0 &&
    splitSaveEnabled(lines, opts.totalCents, locationAllowed, accountAllowed);

  const trySave = (): boolean => {
    if (!splitIsValid()) return false;
    opts.onSave(lines.map((line) => ({ ...line })));
    return true;
  };

  const paint = (focusId: string | null): void => {
    while (root.firstChild) root.removeChild(root.firstChild);
    const table = h("div", { "data-role": "split-lines" });
    lines.forEach((line, index) => {
      table.append(renderLine(line, index));
    });
    const remaining = remainingCents(lines, opts.totalCents);
    const remainingText =
      remaining === null ? "Remaining —" : `Remaining ${formatCents(remaining)}`;
    const exact = remaining === 0n;
    const save = h("button", {
      type: "button",
      "data-role": "split-save",
    }, ["Save split"]);
    save.disabled = !splitIsValid();
    save.addEventListener("click", () => {
      trySave();
    });
    const cancel = h("button", {
      type: "button",
      "data-role": "split-cancel",
    }, ["Cancel"]);
    cancel.addEventListener("click", () => {
      opts.onCancel();
    });
    root.append(
      table,
      h("p", { "data-role": "split-remaining" }, [
        exact ? `${remainingText} ✓` : remainingText,
      ]),
      h("p", { class: "muted" }, [
        lines.length >= MAX_SPLIT_LINES ? "At most 20 lines." : "Tab fills the next line with the remainder.",
      ]),
      h("div", { class: "actions" }, [save, cancel]),
    );
    if (focusId !== null) {
      const field = root.querySelector<HTMLInputElement>(
        `[data-amount-for="${cssEscape(focusId)}"]`,
      );
      field?.focus();
    }
  };

  const renderLine = (line: SplitLineDraft, index: number): HTMLElement => {
    const row = h("div", {
      class: "split-line",
      "data-role": "split-line",
      "data-line-id": line.id,
    });
    row.style.display = "grid";
    row.style.gridTemplateColumns = "1.2fr 1.2fr 1.4fr 0.8fr";
    row.style.gap = "0.5rem";
    row.style.marginBottom = "0.5rem";

    const companyChoices = companyChoiceList(opts.companies);
    const companyLabel = companyChoices.find((choice) => choice.slug === line.company)?.label;
    const companyPick = mountChartChooser({
      kind: "location",
      selectable: companyChoices.map((choice) => choice.label),
      className: "account-input",
      ariaLabel: "Company",
      placeholder: "Company",
      listWords: SPLIT_COMPANY_LIST_WORDS,
      companies: opts.labelCompanies,
      ...(companyLabel !== undefined ? { initialValue: companyLabel } : {}),
    });
    if (unlisted.has(unlistedKey(line.id, "company"))) {
      companyPick.filter.setAttribute("aria-invalid", "true");
    }
    const pickCompany = (label: string | null): void => {
      if (label === null) {
        markUnlisted(line.id, "company", companyPick.filter, line.company.length > 0);
        return;
      }
      const slug = companyChoices.find((choice) => choice.label === label)?.slug;
      if (slug) syncCompany(line.id, slug);
    };
    companyPick.filter.addEventListener("change", () => {
      pickCompany(companyPick.getValue());
    });
    companyPick.filter.addEventListener("blur", () => {
      pickCompany(companyPick.getValue());
    });
    companyPick.root.addEventListener("mousedown", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      const name = target.closest("[data-name]")?.getAttribute("data-name");
      if (name) pickCompany(name);
    });

    const locationPick = mountChartChooser({
      kind: "location",
      selectable: opts.locationsFor(line.company),
      companies: opts.labelCompanies,
      className: "account-input",
      ariaLabel: "Location",
      placeholder: "Location",
      initialValue: line.location,
    });
    if (unlisted.has(unlistedKey(line.id, "location"))) {
      locationPick.filter.setAttribute("aria-invalid", "true");
    }
    const syncLocation = (value: string | null): void => {
      const draft = lines.find((candidate) => candidate.id === line.id);
      if (!draft) return;
      if (value === null) {
        markUnlisted(line.id, "location", locationPick.filter, draft.location.length > 0);
        return;
      }
      unlisted.delete(unlistedKey(line.id, "location"));
      if (value === draft.location) return;
      draft.location = value;
      paint(null);
    };
    locationPick.filter.addEventListener("change", () => {
      syncLocation(locationPick.getValue());
    });
    locationPick.filter.addEventListener("blur", () => {
      syncLocation(locationPick.getValue());
    });
    locationPick.root.addEventListener("mousedown", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      const name = target.closest("[data-name]")?.getAttribute("data-name");
      if (name) syncLocation(name);
    });

    const accountPick = mountChartChooser({
      kind: "account",
      selectable: opts.accountsFor(line.company),
      companies: opts.labelCompanies,
      className: "account-input",
      ariaLabel: "Account",
      placeholder: "Account",
      initialValue: line.account,
    });
    if (unlisted.has(unlistedKey(line.id, "account"))) {
      accountPick.filter.setAttribute("aria-invalid", "true");
    }
    const syncAccount = (value: string | null): void => {
      const draft = lines.find((candidate) => candidate.id === line.id);
      if (!draft) return;
      if (value === null) {
        markUnlisted(line.id, "account", accountPick.filter, draft.account.length > 0);
        return;
      }
      unlisted.delete(unlistedKey(line.id, "account"));
      if (value === draft.account) return;
      draft.account = value;
      paint(null);
    };
    accountPick.filter.addEventListener("change", () => {
      syncAccount(accountPick.getValue());
    });
    accountPick.filter.addEventListener("blur", () => {
      syncAccount(accountPick.getValue());
    });
    accountPick.root.addEventListener("mousedown", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      const name = target.closest("[data-name]")?.getAttribute("data-name");
      if (name) syncAccount(name);
    });

    const amount = h("input", {
      type: "text",
      class: "account-input",
      "data-role": "split-amount",
      "data-amount-for": line.id,
      "aria-label": "Amount",
      value: line.amountText,
      placeholder: "Amount or %",
    });
    amount.addEventListener("input", () => {
      const draft = lines.find((candidate) => candidate.id === line.id);
      if (!draft) return;
      draft.amountText = amount.value;
      const remainingNode = root.querySelector("[data-role=split-remaining]");
      const save = root.querySelector<HTMLButtonElement>("[data-role=split-save]");
      const remaining = remainingCents(lines, opts.totalCents);
      if (remainingNode) {
        if (remaining === null) remainingNode.textContent = "Remaining —";
        else if (remaining === 0n) remainingNode.textContent = `Remaining ${formatCents(remaining)} ✓`;
        else remainingNode.textContent = `Remaining ${formatCents(remaining)}`;
      }
      if (save) {
        save.disabled = !splitIsValid();
      }
    });
    amount.addEventListener("blur", () => {
      const draft = lines.find((candidate) => candidate.id === line.id);
      if (!draft) return;
      draft.amountText = amount.value;
      if (!draft.amountText.trim().endsWith("%")) return;
      lines = applyPercentBlur(lines, opts.totalCents);
      paint(null);
    });
    amount.addEventListener("keydown", (event) => {
      if (event.key === "Tab" && !event.shiftKey) {
        const draft = lines.find((candidate) => candidate.id === line.id);
        if (!draft) return;
        draft.amountText = amount.value;
        const before = lines.length;
        seq += 1;
        const addedId = `split-${String(seq)}`;
        lines = withTabRemainder(lines, index, opts.totalCents, addedId);
        if (lines.length !== before) {
          event.preventDefault();
          paint(addedId);
        }
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        trySave();
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        opts.onCancel();
      }
    });

    row.append(companyPick.root, locationPick.root, accountPick.root, amount);
    return row;
  };

  /** The box holds a name that is not on the list: save is off until it is. */
  const markUnlisted = (
    lineId: string,
    field: "company" | "location" | "account",
    input: HTMLInputElement,
    draftHasValue: boolean,
  ): void => {
    // An empty box on a line that has nothing chosen yet is not an error: the
    // save is already off until a listed name is picked.
    if (input.value.trim().length === 0 && !draftHasValue) return;
    unlisted.add(unlistedKey(lineId, field));
    input.setAttribute("aria-invalid", "true");
    const save = root.querySelector<HTMLButtonElement>("[data-role=split-save]");
    if (save) save.disabled = !splitIsValid();
  };

  const syncCompany = (id: string, value: CompanySlug | null): void => {
    const draft = lines.find((candidate) => candidate.id === id);
    if (!draft || value === null) return;
    unlisted.delete(unlistedKey(id, "company"));
    if (value === draft.company) return;
    draft.company = value;
    if (!opts.locationsFor(value).includes(draft.location)) draft.location = "";
    if (!opts.accountsFor(value).includes(draft.account)) draft.account = "";
    unlisted.delete(unlistedKey(id, "location"));
    unlisted.delete(unlistedKey(id, "account"));
    paint(null);
  };

  paint(null);
  return {
    root,
    drafts: () => lines.map((line) => ({ ...line })),
  };
}

/**
 * The company choices, worded by the one directory (B1c-1, rc-3 row 7).
 *
 * The picker used to append `(<slug>)` to tell two identical display names
 * apart. Owner decision 14 says the slug is never shown, so two companies whose
 * names are the same now read the same here; the label is the only word.
 */
function companyChoiceList(
  companies: readonly { slug: CompanySlug; label: string }[],
): { slug: CompanySlug; label: string }[] {
  const directory = face.createCompanyDirectory(
    companies.map((company) => ({ slug: company.slug, displayName: company.label })),
    { complete: true },
  );
  return companies.map((company) => ({
    slug: company.slug,
    label: face.company(company.slug, directory, "sentence"),
  }));
}

function cssEscape(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }
  return value.replace(/[^a-zA-Z0-9_-]/g, "\\$&");
}
